import { type FC, type ReactNode, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle, InfoCircle } from "@untitledui/icons";
import { Heading as AriaHeading } from "react-aria-components";
import { Dialog, Modal, ModalOverlay } from "../components/application/modals/modal";
import { Button } from "../components/base/buttons/button";
import { CloseButton } from "../components/base/buttons/close-button";
import { TextArea } from "../components/base/textarea/textarea";
import { FeaturedIcon } from "../components/foundations/featured-icon/featured-icon";
import { cx } from "../utils/cx";

export interface ConfirmDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    title: ReactNode;
    description?: ReactNode;
    /** «What happens if you confirm», one line each (spec §14.6.7). */
    consequences?: ReactNode[];
    /** Extra content between the consequences and the buttons (a preview, a summary). */
    children?: ReactNode;
    tone?: "brand" | "error" | "warning" | "success";
    icon?: FC<{ className?: string }>;
    confirmLabel: string;
    cancelLabel?: string;
    /** Ask for a written reason (audit log). The confirm button stays disabled until it is long enough. */
    reason?: { label?: string; placeholder?: string; hint?: string; minLength?: number };
    /** Disable the confirm button for another reason (e.g. a checkbox not ticked). */
    isConfirmDisabled?: boolean;
    /** May return a promise; the dialog shows a spinner and closes when it resolves. */
    onConfirm: (reason: string) => void | Promise<unknown>;
    size?: "sm" | "md" | "lg";
}

const toneIcon = { brand: InfoCircle, error: AlertTriangle, warning: AlertTriangle, success: CheckCircle } as const;
const widths = { sm: "sm:max-w-100", md: "sm:max-w-120", lg: "sm:max-w-160" } as const;

/**
 * Confirmation modal (Untitled UI modal anatomy) that always spells out the consequences of the
 * action, and optionally collects the reason that goes into the audit log.
 */
export const ConfirmDialog = ({
    isOpen,
    onOpenChange,
    title,
    description,
    consequences,
    children,
    tone = "brand",
    icon,
    confirmLabel,
    cancelLabel = "انصراف",
    reason,
    isConfirmDisabled,
    onConfirm,
    size = "md",
}: ConfirmDialogProps) => {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const minLength = reason?.minLength ?? 5;
    const reasonOk = !reason || text.trim().length >= minLength;

    useEffect(() => {
        if (!isOpen) {
            setText("");
            setBusy(false);
        }
    }, [isOpen]);

    const confirm = async () => {
        if (!reasonOk) return;
        setBusy(true);
        try {
            await onConfirm(text.trim());
            onOpenChange(false);
        } catch {
            // The caller shows its own error toast; keep the dialog open so nothing typed is lost.
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange} isDismissable={!busy}>
            <Modal className={widths[size]}>
                <Dialog aria-label={typeof title === "string" ? title : "تأیید"}>
                    <div className="relative flex w-full flex-col gap-5 p-4 sm:p-6">
                        <CloseButton onPress={() => onOpenChange(false)} size="lg" label="بستن" className="absolute top-3 end-3" />
                        <FeaturedIcon icon={icon ?? toneIcon[tone]} color={tone} theme="light" size="lg" />
                        <div className="flex flex-col gap-1.5 pe-8">
                            <AriaHeading slot="title" className="text-lg font-semibold text-primary">
                                {title}
                            </AriaHeading>
                            {description && <div className="text-sm text-tertiary">{description}</div>}
                        </div>

                        {consequences && consequences.length > 0 && (
                            <div className="flex flex-col gap-2 rounded-xl bg-secondary p-4">
                                <p className="text-xs font-semibold text-tertiary">با تأیید این کار:</p>
                                <ul className="flex flex-col gap-1.5 text-sm text-secondary">
                                    {consequences.map((c, i) => (
                                        <li key={i} className="flex gap-2">
                                            <span
                                                className={cx(
                                                    "mt-2 size-1.5 shrink-0 rounded-full",
                                                    tone === "error" ? "bg-fg-error-secondary" : "bg-fg-brand-secondary",
                                                )}
                                                aria-hidden="true"
                                            />
                                            <span>{c}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {children}

                        {reason && (
                            <TextArea
                                label={reason.label ?? "دلیل (در تاریخچهٔ تغییرات ثبت می‌شود)"}
                                placeholder={reason.placeholder ?? "دلیل این تصمیم را کوتاه بنویسید…"}
                                hint={reason.hint ?? `دست‌کم ${minLength} کاراکتر. این متن بعداً قابل ویرایش نیست.`}
                                isRequired
                                rows={3}
                                value={text}
                                onChange={setText}
                            />
                        )}

                        <div className="flex flex-col-reverse gap-3 pt-1 sm:flex-row sm:justify-end">
                            <Button color="secondary" size="lg" onPress={() => onOpenChange(false)} isDisabled={busy}>
                                {cancelLabel}
                            </Button>
                            <Button
                                color={tone === "error" ? "primary-destructive" : "primary"}
                                size="lg"
                                onPress={confirm}
                                isLoading={busy}
                                showTextWhileLoading
                                isDisabled={!reasonOk || isConfirmDisabled}
                            >
                                {confirmLabel}
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
};
